import React, { useId, useRef, useState } from 'react';
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import Logo from './Logo';
import UserMenu from './UserMenu';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './notifications/NotificationBell';
import { useSidebar } from '../contexts/SidebarContext';
import { RAIL_WIDTH_TRANSITION, railWidth } from './sidebar/railMotion';
import { useRailHandoff, useSidebarShortcut } from './sidebar/useSidebarRail';

interface LayoutProps {
  children: React.ReactNode;
  /**
   * Use the whole width next to the sidebar instead of the centred 80rem
   * column — for workspaces like Verläufe, where the collapsed rail's room
   * must reach the content and not end up as empty margins.
   */
  fullWidth?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, fullWidth = false }) => {
  const [open, setOpen] = useState(false);
  const { collapsed, toggle, shownRef } = useSidebar();
  const railRef = useRef<HTMLElement>(null);
  const railId = useId();
  const rail = useRailHandoff(collapsed, shownRef, railRef);
  useSidebarShortcut(toggle);

  return (
    <div className="h-screen flex flex-col bg-chrome">
      <TopBar />

      {/* Header for Mobile/Tablet & Desktop Logo Area - same chrome tone as the sidebar, no seam */}
      <header className="w-full bg-chrome">
        <div className="flex h-16 items-center px-4 md:pl-0 w-full justify-between">
           <div className="flex items-center">
             {/* Mobile Menu Trigger */}
             <Sheet open={open} onOpenChange={setOpen}>
               <SheetTrigger asChild>
                 <Button variant="ghost" className="mr-4 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden">
                   <Menu className="h-6 w-6" />
                   <span className="sr-only">Toggle Menu</span>
                 </Button>
               </SheetTrigger>
               {/* 272px: wide enough for the longest entry, "TimeGlobe Intelligence", inside its highlight. */}
               <SheetContent side="left" className="pr-0 w-[272px] flex flex-col bg-chrome">
                 <div className="px-6 mb-2 mt-4 shrink-0">
                   <Logo />
                 </div>
                 <div className="flex-1 overflow-y-auto">
                   <Sidebar onNavigate={() => setOpen(false)} />
                 </div>
               </SheetContent>
             </Sheet>

             {/* Desktop Logo - a slot exactly as wide as the rail below, gliding
                 with it: collapsed, only the round signet is left. 13.5px puts
                 the 45px signet's centre on the icon column (36px) in both
                 states, so the signet never moves. */}
             <div
               className={cn(
                 "hidden md:flex mt-2 items-center overflow-hidden pl-[13.5px]",
                 RAIL_WIDTH_TRANSITION,
                 railWidth(rail),
               )}
             >
                <Logo compact={rail} />
             </div>

             {/* Mobile Logo */}
             <div className="flex md:hidden mt-2">
                <Logo />
             </div>
           </div>

           <div className="flex items-center gap-1">
              <NotificationBell />
              <ThemeToggle />
              <UserMenu />
           </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar - merges with the header above, same chrome tone.
            Full (icons + texts) or the narrow icon rail; the content glides along. */}
        <aside
          ref={railRef}
          id={railId}
          aria-label="Seitenleiste"
          data-sidebar="rail"
          data-state={rail ? 'collapsed' : 'expanded'}
          className={cn(
            "hidden shrink-0 flex-col bg-chrome md:flex overflow-y-auto overflow-x-hidden",
            // Short windows scroll the rail (wheel, touch, focus); a visible
            // scrollbar would take its width out of the 72px icon column.
            "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            RAIL_WIDTH_TRANSITION,
            railWidth(rail),
          )}
        >
          <Sidebar collapsed={rail} onToggleCollapsed={toggle} railId={railId} />
        </aside>

        {/* Main Content - rounded top-left corner reveals the chrome tone behind it */}
        <main className="flex-1 min-w-0 w-full overflow-y-auto bg-background rounded-tl-[28px]">
          <div
            className={cn(
              "flex h-full min-h-0 flex-col p-4 md:p-8 pt-6",
              fullWidth ? "w-full" : "container mx-auto max-w-7xl",
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
